import { PROCESS_SUBMIT_PROCESS_MESSAGE } from '../constants';
import { useEffect, useState } from 'react';

interface TGlobalLoaderProps {
  loading: boolean;
  message?: string;
}

const GlobalLoader = ({
  loading,
  message = PROCESS_SUBMIT_PROCESS_MESSAGE,
}: TGlobalLoaderProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!loading) {
      setProgress(0);
      return;
    }

    // Simulate progress for better UX
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev; // Cap at 90% until loading completes
        return prev + Math.random() * 15;
      });
    }, 300);

    return () => clearInterval(interval);
  }, [loading]);

  if (!loading) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/80 backdrop-blur-sm">
      {/* Main loader card - subtle and minimal */}
      <div className="relative bg-white rounded-xl shadow-lg border border-slate-200/80 p-6 md:p-8 w-full max-w-sm mx-4">
        {/* Header with icon */}
        <div className="flex flex-col items-center mb-6">
          {/* Subtle spinner */}
          <div className="relative w-12 h-12 mb-5">
            {/* Single rotating ring - very subtle */}
            <div className="absolute inset-0 border-2 border-slate-200 rounded-full"></div>
            <div
              className="absolute inset-0 border-2 border-transparent border-t-slate-400 rounded-full animate-spin"
              style={{ animationDuration: '1s' }}
            ></div>

            {/* Center dot - minimal */}
            <div className="absolute inset-3 bg-slate-300 rounded-full"></div>
          </div>

          {/* Title - subtle */}
          <h3 className="text-base font-medium text-slate-700 mb-1.5 tracking-tight">
            Processing
          </h3>

          {/* Message - subtle */}
          <p className="text-xs text-slate-500 text-center leading-relaxed max-w-xs">
            {message}
          </p>
        </div>

        {/* Progress bar - minimal */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs font-normal text-slate-400">Progress</span>
            <span className="text-xs font-medium text-slate-600">
              {Math.min(Math.round(progress), 90)}%
            </span>
          </div>

          {/* Progress track - subtle */}
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-slate-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.min(progress, 90)}%` }}
            ></div>
          </div>
        </div>

        {/* Loading dots - very subtle */}
        <div className="flex justify-center items-center gap-1.5">
          <div
            className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"
            style={{ animationDelay: '0s', animationDuration: '1.2s' }}
          ></div>
          <div
            className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"
            style={{ animationDelay: '0.15s', animationDuration: '1.2s' }}
          ></div>
          <div
            className="w-1 h-1 bg-slate-400 rounded-full animate-bounce"
            style={{ animationDelay: '0.3s', animationDuration: '1.2s' }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default GlobalLoader;
